import { useEffect, useMemo, useState } from "react";
import {
  archiveAnnouncement,
  createAnnouncement,
  getAnnouncementOverview,
  updateAnnouncement,
  updateAnnouncementStatus,
} from "../api/instateamAdminApi";
import { AppIcon, MetricCard } from "../components/InstateamMasterComponents";

const INITIAL_FORM = {
  title: "",
  description: "",
  type: "General",
  audienceType: "all",
  departments: "",
  employees: "",
  status: "published",
  publishAt: "",
  expiryAt: "",
  pinned: false,
  channels: { inApp: true },
};

const STATUS_TONES = {
  published: "active",
  scheduled: "pending",
  expired: "inactive",
  draft: "muted",
};

function StatusBadge({ status }) {
  return <span className={`status-badge ${STATUS_TONES[status] || "pending"}`}>{status}</span>;
}

function formatDate(value) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function toInputDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 16);
}

function toList(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function buildPayload(form) {
  return {
    ...form,
    channels: { inApp: true },
    departments: toList(form.departments),
    employees: toList(form.employees),
    publishAt: form.publishAt || null,
    expiryAt: form.expiryAt || null,
  };
}

export default function AnouncementsPage({
  token,
  employees = [],
  departmentOptions = [],
  searchText = "",
  onSearchChange,
  onProtectedError,
  onNotify,
}) {
  const [showCreator, setShowCreator] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [editingAnnouncementId, setEditingAnnouncementId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  async function fetchAnnouncements() {
    if (!token) return;

    try {
      setIsLoading(true);
      const payload = await getAnnouncementOverview(token, searchText);
      setAnnouncements(payload.announcements || []);
      setMetrics(payload.metrics || null);
    } catch (error) {
      if (!onProtectedError?.(error)) {
        onNotify?.({ type: "error", text: error.message });
      }
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchAnnouncements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, searchText]);

  useEffect(() => {
    if (!token) return undefined;

    const refreshWhenFocused = () => {
      if (!document.hidden) {
        fetchAnnouncements();
      }
    };
    const intervalId = window.setInterval(refreshWhenFocused, 10000);
    window.addEventListener("focus", refreshWhenFocused);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshWhenFocused);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, searchText]);

  const metricCards = useMemo(() => {
    const total = metrics?.total ?? announcements.length;
    const published = metrics?.published ?? announcements.filter((item) => item.status === "published").length;
    const scheduled = metrics?.scheduled ?? announcements.filter((item) => item.status === "scheduled").length;
    const expired = metrics?.expired ?? announcements.filter((item) => item.status === "expired").length;

    return [
      { label: "Total Announcements", value: total, helper: "Current admin list", icon: "announcements", tone: "yellow" },
      { label: "Published", value: published, helper: "Visible to employees", icon: "activeUsers", tone: "green" },
      { label: "Scheduled", value: scheduled, helper: "Queued for later", icon: "calendar", tone: "blue" },
      { label: "Expired", value: expired, helper: "No longer visible", icon: "complaints", tone: "red" },
    ];
  }, [announcements, metrics]);

  function updateForm(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  // Push and email delivery are intentionally disabled; announcements are in-app only.

  function resetForm() {
    setForm(INITIAL_FORM);
    setEditingAnnouncementId(null);
  }

  async function handleSubmit(event) {
    event.preventDefault();

    try {
      setIsSaving(true);
      const payload = buildPayload(form);
      if (editingAnnouncementId) {
        await updateAnnouncement(token, editingAnnouncementId, payload);
        onNotify?.({ type: "success", text: "Announcement updated successfully." });
      } else {
        await createAnnouncement(token, payload);
        onNotify?.({ type: "success", text: "Announcement created successfully." });
      }

      resetForm();
      await fetchAnnouncements();
    } catch (error) {
      if (!onProtectedError?.(error)) {
        onNotify?.({ type: "error", text: error.message });
      }
    } finally {
      setIsSaving(false);
    }
  }

  function handleEdit(announcement) {
    setEditingAnnouncementId(announcement._id);
    setForm({
      title: announcement.title || "",
      description: announcement.description || "",
      type: announcement.type || "General",
      audienceType: announcement.audienceType || "all",
      departments: (announcement.departments || []).join(", "),
      employees: (announcement.employees || []).join(", "),
      status: announcement.storedStatus || announcement.status || "draft",
      publishAt: toInputDateTime(announcement.publishAt),
      expiryAt: toInputDateTime(announcement.expiryAt),
      pinned: Boolean(announcement.pinned),
      channels: {
        inApp: true,
      },
    });
    setShowCreator(true);
  }

  async function handleArchive(announcement) {
    if (!window.confirm(`Archive "${announcement.title}"?`)) return;

    try {
      await archiveAnnouncement(token, announcement._id);
      onNotify?.({ type: "success", text: "Announcement archived successfully." });
      await fetchAnnouncements();
    } catch (error) {
      if (!onProtectedError?.(error)) {
        onNotify?.({ type: "error", text: error.message });
      }
    }
  }

  async function handleStatusChange(announcement, status) {
    try {
      await updateAnnouncementStatus(token, announcement._id, status);
      onNotify?.({ type: "success", text: "Announcement status updated." });
      await fetchAnnouncements();
    } catch (error) {
      if (!onProtectedError?.(error)) {
        onNotify?.({ type: "error", text: error.message });
      }
    }
  }

  function downloadCsv() {
    if (!announcements.length) {
      onNotify?.({ type: "warning", text: "No announcements available to export." });
      return;
    }

    const rows = [
      ["Title", "Type", "Audience", "Status", "Pinned", "Publish Date", "Read Count", "Acknowledged"],
      ...announcements.map((item) => [
        item.title,
        item.type,
        item.audience,
        item.status,
        item.pinned ? "Yes" : "No",
        String(item.publishAt || item.createdAt || "").slice(0, 10) || "Not set",
        item.readCount || 0,
        item.acknowledgedCount || 0,
      ]),
    ];

    const csv = rows
      .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "instateam-announcements.csv";
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <section className="announcements-page">
      <div className="page-action-row">
        <div>
          <h3>Announcement Center</h3>
          <p>Create, schedule, and manage employee-facing company updates.</p>
        </div>
        <div className="page-actions">
          <button className="secondary-action" type="button" onClick={downloadCsv}>
            <AppIcon name="export" />
            Export
          </button>
          <button className="primary-action" type="button" onClick={() => setShowCreator((value) => !value)}>
            <AppIcon name="plus" />
            {showCreator ? "Hide Composer" : "Create Announcement"}
          </button>
        </div>
      </div>

      <div className="metrics-grid">
        {metricCards.map((metric) => <MetricCard key={metric.label} {...metric} />)}
      </div>

      <div className="announcement-layout">
        <article className="data-card announcement-list-card">
          <div className="data-card-header">
            <div>
              <h3>All Announcements</h3>
              <p>{isLoading ? "Loading announcements..." : "Manage published, scheduled, and expired announcements."}</p>
            </div>
            <div className="inline-search">
              <AppIcon name="search" />
              <input value={searchText} onChange={(event) => onSearchChange?.(event.target.value)} placeholder="Search announcement..." />
            </div>
          </div>

          <div className="responsive-table">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Announcement</th>
                  <th>Type</th>
                  <th>Audience</th>
                  <th>Date</th>
                  <th>Read</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {announcements.map((item) => (
                  <tr key={item._id}>
                    <td>
                      <div className="announcement-title-cell">
                        <span className="announcement-icon"><AppIcon name="announcements" /></span>
                        <div>
                          <strong>{item.pinned ? "Pinned: " : ""}{item.title}</strong>
                          <small>{item.description}</small>
                        </div>
                      </div>
                    </td>
                    <td><span className="announcement-type-badge">{item.type}</span></td>
                    <td>{item.audience}</td>
                    <td>{formatDate(item.publishAt || item.createdAt)}</td>
                    <td><strong className="read-count">{item.readCount || 0} read / {item.acknowledgedCount || 0} ack</strong></td>
                    <td><StatusBadge status={item.status} /></td>
                    <td>
                      <div className="table-actions">
                        <button type="button" aria-label={`View ${item.title}`} onClick={() => window.alert(`${item.title}\n\n${item.description}`)}>
                          <AppIcon name="eye" />
                        </button>
                        <button type="button" aria-label={`Edit ${item.title}`} onClick={() => handleEdit(item)}>
                          <AppIcon name="edit" />
                        </button>
                        <button type="button" aria-label={`Publish ${item.title}`} onClick={() => handleStatusChange(item, "published")}>
                          <AppIcon name="activeUsers" />
                        </button>
                        <button type="button" aria-label={`Archive ${item.title}`} onClick={() => handleArchive(item)}>
                          <AppIcon name="close" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!announcements.length ? (
                  <tr>
                    <td className="empty-table-state" colSpan="7">{isLoading ? "Loading announcements..." : "No announcements found."}</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </article>

        {showCreator ? (
          <form className="data-card announcement-composer" onSubmit={handleSubmit}>
            <div className="data-card-header compact">
              <div>
                <h3>{editingAnnouncementId ? "Edit Announcement" : "Create Announcement"}</h3>
                <p>Write and publish updates for employees.</p>
              </div>
              <span className="announcement-icon large"><AppIcon name="announcements" /></span>
            </div>

            <div className="announcement-composer-grid">
              <div className="announcement-composer-main">
                <label className="form-field">
                  <span>Announcement Title</span>
                  <div className="field-shell">
                    <input value={form.title} onChange={(event) => updateForm("title", event.target.value)} placeholder="Enter announcement title" />
                  </div>
                </label>
                <label className="form-field">
                  <span>Description</span>
                  <textarea className="announcement-textarea" value={form.description} onChange={(event) => updateForm("description", event.target.value)} placeholder="Write announcement details here..." />
                </label>
              </div>

              <div className="announcement-composer-side">
                <div className="two-column-fields">
                  <label className="form-field">
                    <span>Type</span>
                    <div className="field-shell select-shell">
                      <select value={form.type} onChange={(event) => updateForm("type", event.target.value)}>
                        <option>General</option>
                        <option>Policy</option>
                        <option>Event</option>
                        <option>Holiday</option>
                        <option>HR</option>
                        <option>Finance</option>
                        <option>Compliance</option>
                      </select>
                      <AppIcon name="chevronDown" />
                    </div>
                  </label>
                  <label className="form-field">
                    <span>Status</span>
                    <div className="field-shell select-shell">
                      <select value={form.status} onChange={(event) => updateForm("status", event.target.value)}>
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="expired">Expired</option>
                      </select>
                      <AppIcon name="chevronDown" />
                    </div>
                  </label>
                </div>

                <label className="form-field">
                  <span>Audience</span>
                  <div className="field-shell select-shell">
                    <select value={form.audienceType} onChange={(event) => updateForm("audienceType", event.target.value)}>
                      <option value="all">All Employees</option>
                      <option value="departments">Department Wise</option>
                      <option value="employees">Selected Employees</option>
                    </select>
                    <AppIcon name="chevronDown" />
                  </div>
                </label>

                {form.audienceType === "departments" ? (
                  <label className="form-field">
                    <span>Departments</span>
                    <div className="field-shell">
                      <input value={form.departments} onChange={(event) => updateForm("departments", event.target.value)} list="announcement-departments" placeholder="HR, Finance, Development" />
                      <datalist id="announcement-departments">
                        {departmentOptions.map((department) => <option key={department} value={department} />)}
                      </datalist>
                    </div>
                  </label>
                ) : null}

                {form.audienceType === "employees" ? (
                  <label className="form-field">
                    <span>Employee IDs</span>
                    <div className="field-shell">
                      <input value={form.employees} onChange={(event) => updateForm("employees", event.target.value)} list="announcement-employees" placeholder="Paste database employee ids, comma separated" />
                      <datalist id="announcement-employees">
                        {employees.map((employee) => (
                          <option key={employee._id} value={employee._id} label={`${employee.fullName} (${employee.employeeId})`} />
                        ))}
                      </datalist>
                    </div>
                  </label>
                ) : null}

                <div className="two-column-fields announcement-date-fields">
                  <label className="form-field">
                    <span>Publish Date</span>
                    <div className="field-shell">
                      <input type="datetime-local" value={form.publishAt} onChange={(event) => updateForm("publishAt", event.target.value)} />
                    </div>
                  </label>
                  <label className="form-field">
                    <span>Expiry Date</span>
                    <div className="field-shell">
                      <input type="datetime-local" value={form.expiryAt} onChange={(event) => updateForm("expiryAt", event.target.value)} />
                    </div>
                  </label>
                </div>

                <div className="announcement-channel-row">
                  <label><input type="checkbox" checked readOnly /> In-app Alert</label>
                  {/* Push and email delivery are hidden for now; announcements stay in-app only. */}
                  <label><input type="checkbox" checked={form.pinned} onChange={(event) => updateForm("pinned", event.target.checked)} /> Pinned</label>
                </div>

                <div className="composer-actions">
                  <button className="secondary-action" type="button" onClick={resetForm}>Clear</button>
                  <button className="primary-action" type="submit" disabled={isSaving}>{isSaving ? "Saving..." : editingAnnouncementId ? "Update" : "Publish"}</button>
                </div>
              </div>
            </div>
          </form>
        ) : null}
      </div>
    </section>
  );
}
